import Configuration from '@/configuration/sources/SetFormConfig.yml'
import { isString } from 'underscore'
import { getPathValue, setValueToPath, deletePath } from '@/common/util/object'
import { getI18nProperties } from '@/store/utils/labels'

/**
 * Normalizes form configuration for backward compatibility.
 * - Maps legacy value formats to the current expected format (ex: strings -> i18n objects)
 * - Applies property aliases (legacy keys -> current keys)
 */
export default class ConfigurationMapper {
  constructor($store) {
    this.$store = $store
  }

  /**
   * Updates the form config object mapping certain values if necessary.
   *
   * @param {object} formConfig
   * @returns {object} Updated form config object
   */
  map(formConfig) {
    formConfig = this.mapPropertyAliases(formConfig)
    const mappers = Configuration.mappers

    // Check for mappers
    for (const [key, mapper] of Object.entries(mappers)) {
      const legacyValue = getPathValue(formConfig, key)
      if (legacyValue !== undefined) {
        formConfig = this[mapper](legacyValue, key, formConfig)
      }
    }

    return formConfig
  }

  /**
   * Maps the string legacy value into the new format with the current language.
   *
   * @param {string} legacyValue Value to be parsed into new format
   * @param {string} path Path in the config object
   * @param {object} formConfig Form config update object
   * @returns {object} Updated form config object
   */
  stringToContentLangString(legacyValue, path, formConfig) {
    if (!isString(legacyValue)) return formConfig

    formConfig = setValueToPath(
      formConfig,
      path,
      getI18nProperties(legacyValue)
    )

    return formConfig
  }

  /**
   * Maps the [fieldName]: string legacy value into the new format with the current language.
   *
   * @param {string} legacyValue Value to be parsed into new format
   * @param {string} path Path in the config object
   * @param {object} formConfig Form config update object
   * @returns {object} Updated form config object
   */
  fieldNameStringToContentLangString(legacyValue, path, formConfig) {
    const resolveFieldValue = (fieldName, value) =>
      fieldName === 'content' ? value : getI18nProperties(value)
    // Check field values
    for (const fieldName in legacyValue) {
      const value = legacyValue[fieldName]
      if (!isString(value)) continue

      formConfig = setValueToPath(
        formConfig,
        `${path}.${fieldName}`,
        resolveFieldValue(fieldName, value)
      )
    }

    return formConfig
  }

  /**
   * Maps the content label legacy value into the new format with the current language.
   *
   * @param {object} legacyValue Value to be parsed into new format
   * @param {string} path Path in the config object
   * @param {object} formConfig Form config update object
   * @returns {object} Updated form config object
   */
  fieldNameContentLabelStringToContentLangString(
    legacyValue,
    path,
    formConfig
  ) {
    for (const fieldName in legacyValue) {
      for (const type in legacyValue[fieldName]) {
        if (!isString(legacyValue[fieldName][type])) continue

        formConfig = setValueToPath(
          formConfig,
          `${path}.${fieldName}.${type}`,
          getI18nProperties(legacyValue[fieldName][type])
        )
      }
    }

    return formConfig
  }

  /**
   * Maps the [fieldName]: string legacy value into the new format with the current language.
   * For installmentNumber uses the specific format with { default, singlePayment, singlePaymentCredit }
   *
   * @param {string} legacyValue Value to be parsed into new format
   * @param {string} path Path in the config object
   * @param {object} formConfig Form config update object
   * @returns {object} Updated form config object
   */
  fieldNamePlaceholderStringToContentLangString(legacyValue, path, formConfig) {
    // Check field values
    for (const fieldName in legacyValue) {
      if (fieldName === 'installmentNumber') {
        for (const type in legacyValue[fieldName]) {
          if (!isString(legacyValue[fieldName][type])) continue

          formConfig = setValueToPath(
            formConfig,
            `${path}.${fieldName}.${type}`,
            getI18nProperties(legacyValue[fieldName][type])
          )
        }
      } else {
        if (!isString(legacyValue[fieldName])) continue
        formConfig = setValueToPath(
          formConfig,
          `${path}.${fieldName}`,
          getI18nProperties(legacyValue[fieldName])
        )
      }
    }

    return formConfig
  }

  /**
   * Maps the payment method label legacy value into the new format with the current language.
   *
   * @param {object} legacyValue Value to be parsed into new format
   * @param {string} path Path in the config object
   * @param {object} formConfig Form config update object
   * @returns {object} Updated form config object
   */
  paymentMethodLabelToContentLangString(legacyValue, path, formConfig) {
    // Check payment method values
    for (const method in legacyValue) {
      if (!isString(legacyValue[method].label)) continue

      formConfig = setValueToPath(
        formConfig,
        `${path}.${method}.label`,
        getI18nProperties(legacyValue[method].label)
      )
    }

    return formConfig
  }

  /**
   * Maps legacy keys to their current equivalents.
   * Legacy keys are removed from the update object.
   *
   * @param {object} formConfig Form config update object
   * @returns {object} Updated form config object
   */
  mapPropertyAliases(formConfig) {
    const aliases = Configuration.propertyAliases

    aliases.forEach(({ legacy: legacyPath, current: currentPath }) => {
      const legacyValue = getPathValue(formConfig, legacyPath)
      const hasLegacy = legacyValue !== undefined

      if (!hasLegacy) return

      console.warn(
        `The config key "${legacyPath}" is deprecated. Please use "${currentPath}" instead.`
      )

      formConfig = setValueToPath(formConfig, currentPath, legacyValue)
      deletePath(formConfig, legacyPath)
    })

    return formConfig
  }
}
